import { Injectable } from "@angular/core";
import { ConceptType, PersonRole } from "@ha/data/basic";
import { BaseValidationResult } from "@ha/ui/forms";
import { Action, State, StateContext } from "@ngxs/store";
import produce from "immer";
import { Observable, tap } from "rxjs";

import { CertificateStatus } from "../../permit-create/models/certificate-status.enum";
import { PermitPerson, PermitPersonWithStatus, RecentTeamMembersResponse } from "../../shared/models/permit-person.model";
import { CountersignPermitResponse, CreateCertificateAndPersonResponse, CreatePermitTeamTextMemberResponse } from "../models/create-certificate-person.model";
import { AddStagedPersonsToPermitCommand, AddStagedPersonsToPermitResponse, FindCertifiedPersonQuery, FindCertifiedPersonResult, NewPersonData, PermitTeamTextMember, PersonInfo, SearchPersonForPermitCommand, SearchPersonForPermitResponse } from "../models/permit-team-commands.model";
import { PermitTeam } from "../models/permit-team.model";
import { PersonSearchState } from "../models/person-search-state.model";
import { PermitTeamService } from "../services/permit-team.service";
import { PermitTeamActions } from "./permit-team.action";

export interface PermitTeamStateModel {
    certificateIdentifier: string;
    recentTeamMembers: PermitPersonWithStatus[];
    recentTeamMembersSearchResult: PermitPersonWithStatus[];
    recentTeamMembersFullyLoaded: boolean;
    hasColleagueMembers: boolean;
    hasSharedFromPremiumMembers: boolean;
    search: PermitTeamSearch;
    stagedPersons: PermitPerson[];
    stageRole: PersonRole;
    team: PermitTeam;
    currentConcept: ConceptType;
    newPersonForm: { creating: boolean; model: PersonInfo; dirty: boolean; status: string; errors: unknown; };
    newPermitTeamTextMemberForm: {
        creating: boolean;
        model: PermitTeamTextMember;
        dirty: boolean;
        status: string;
        errors: unknown;
    };
    newPersonFormResponse: SearchPersonForPermitResponse;
    createCertificateFormResponse: CreateCertificateAndPersonResponse;
}

export interface PermitTeamSearch {
    errorMessage: string;
    query: FindCertifiedPersonQuery;
    command: SearchPersonForPermitCommand;
    person: PermitPersonWithStatus;
    searching: boolean;
}

const searchDefaults: PermitTeamSearch = {
    errorMessage: undefined,
    query: undefined,
    command: undefined,
    person: undefined,
    searching: false,
};

const teamDefaults: PermitTeam = {
    canAddMember: true,
    fireWatchers: undefined,
    fireWatchersNotNeeded: false,
    hotWorkers: undefined,
    permitIssuer: undefined,
    superintendent: undefined,
    postFireWatchers: undefined,
    observers: undefined,
    allowSwitching: false,
    countersignPermits: undefined,
};

const newPersonFormDefaults = {
    creating: false,
    model: undefined,
    dirty: false,
    status: "",
    errors: {},
};

const newPermitTeamTextMemberFormDefaults = {
    creating: false,
    model: undefined,
    dirty: false,
    status: "",
    errors: {},
};

@Injectable()
@State<PermitTeamStateModel>({
    name: "permitTeam",
    defaults: {
        certificateIdentifier: undefined,
        recentTeamMembers: undefined,
        recentTeamMembersSearchResult: undefined,
        recentTeamMembersFullyLoaded: undefined,
        hasColleagueMembers: undefined,
        hasSharedFromPremiumMembers: undefined,
        stageRole: undefined,
        search: searchDefaults,
        stagedPersons: undefined,
        currentConcept: undefined,
        team: teamDefaults,
        newPersonForm: newPersonFormDefaults,
        newPermitTeamTextMemberForm: newPermitTeamTextMemberFormDefaults,
        newPersonFormResponse: undefined,
        createCertificateFormResponse: undefined,
    },
})
export class PermitTeamState {
    constructor(private permitTeamService: PermitTeamService) {}

    @Action(PermitTeamActions.AddStagedPersonsToPermit)
    public addStagedPersonsToPermit(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.AddStagedPersonsToPermit,
    ): Observable<AddStagedPersonsToPermitResponse> {
        const currentState = ctx.getState();
        const command: AddStagedPersonsToPermitCommand = {
            permitId: action.permitId,
            personsCertificateId: currentState.stagedPersons.map((_) => _.certificateId) ?? null,
            textPersonNames: currentState.stagedPersons.map((_) => _.firstName),
            role: action.role,
        };

        return this.permitTeamService.addStagedPersonsToPermit(command).pipe(tap({
            next: () => {
                ctx.dispatch(new PermitTeamActions.GetTeam(action.permitId));
            },
            // Need to clear staged persons in case of error as well
            complete: () => {
                ctx.setState(produce((draft) => {
                    draft.stagedPersons = undefined;
                    draft.stageRole = undefined;
                }));
            },
        }));
    }

    @Action(PermitTeamActions.ClearCreatePersonFormData)
    public clearCreatePersonFormData(ctx: StateContext<PermitTeamStateModel>): void {
        ctx.setState(produce((draft) => {
            draft.newPersonForm = newPersonFormDefaults;
        }));
    }

    @Action(PermitTeamActions.CreateCertificateAndStagePerson)
    public createCertificateAndStagePerson(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.CreateCertificateAndStagePerson,
    ): Observable<SearchPersonForPermitResponse> {
        const command = {} as SearchPersonForPermitCommand;

        const person = ctx.getState().newPersonForm.model;
        const concept = ctx.getState().currentConcept;

        command.permitId = action.permitId;
        if (concept == ConceptType.BrandfarligaArbeten) {
            if (person.hasForeignPersonalNumber) {
                command.currentState = PersonSearchState.Concept2NoSwedishPersonNumber.toString();
                command.concept2NoSwedishPersonNumberData = {
                    personInfo: person,
                    certificateIdentifier: person.certificateIdentifier,
                    concept: concept,
                    certificateExpirationDate: person.expirationDate,
                } as NewPersonData;
            } else if (person.personId != 0) {
                command.currentState = PersonSearchState.Concept2ByPersonNumberFound.toString();
                command.concept2ByPersonNumberFoundData = {
                    personInfo: person,
                    certificateIdentifier: person.certificateIdentifier,
                    concept: concept,
                    certificateExpirationDate: person.expirationDate,
                } as NewPersonData;
            } else {
                command.currentState = PersonSearchState.Concept2ByPersonNumberNotFound.toString();
                command.concept2ByPersonNumberNotFoundData = {
                    personInfo: person,
                    certificateIdentifier: person.certificateIdentifier,
                    concept: concept,
                    certificateExpirationDate: person.expirationDate,
                } as NewPersonData;
            }
        }

        if (concept == ConceptType.Denmark) {
            command.currentState = PersonSearchState.DenmarkByEmailNotFound.toString();
            command.otherCountryNotFoundData = {
                personInfo: person,
                certificateIdentifier: person.certificateIdentifier,
                concept: concept,
                certificateExpirationDate: person.expirationDate,
            } as NewPersonData;
        }

        if (concept == ConceptType.Norway) {
            command.currentState = PersonSearchState.NorwayByShortNumberNotFound.toString();
            command.otherCountryNotFoundData = {
                personInfo: person,
                certificateIdentifier: person.certificateIdentifier,
                concept: concept,
                certificateExpirationDate: person.expirationDate,
            } as NewPersonData;
        }

        if (concept == ConceptType.Finland) {
            command.currentState = PersonSearchState.FinlandByShortNumberNotFound.toString();
            command.otherCountryNotFoundData = {
                personInfo: person,
                certificateIdentifier: person.certificateIdentifier,
                concept: concept,
                certificateExpirationDate: person.expirationDate,
            } as NewPersonData;
        }

        if (concept == ConceptType.Other) {
            command.currentState = PersonSearchState.OtherCountry.toString();
            command.otherCountryNotFoundData = {
                personInfo: person,
                certificateIdentifier: person.certificateIdentifier,
                concept: concept,
                certificateExpirationDate: person.expirationDate,
            } as NewPersonData;
        }

        ctx.setState(produce((draft) => {
            draft.newPersonForm.creating = true;
        }));

        return this.permitTeamService.searchPersonForPermit(command).pipe(tap({
            next: (response: SearchPersonForPermitResponse) => {
                if (response.success) {
                    ctx.dispatch(
                        new PermitTeamActions.StagePerson({
                            birthDate: person.birthDate,
                            certificateId: response.person.certificateId,
                            certificateIdentifier: person.certificateIdentifier,
                            concept: person.concept,
                            email: person.email,
                            expirationDate: person.expirationDate,
                            hasValidCertificate: true,
                            firstName: person.firstName,
                            lastName: person.lastName,
                            mobile: person.mobile,
                            personId: response.person.personId,
                            personalNumber: person.personalNumber,
                            hasForeignPersonalNumber: person.hasForeignPersonalNumber,
                            signature: undefined,
                            endSignature: undefined,
                            hasAcceptedUserAgreement: false,
                            isPremiumIssuer: false,
                            isPremiumSuperintendent: false,
                            isFromColleague: false,
                            isSharedFromPremium: false,
                            allowExternalCertificates: false,
                            isTextMember: false,
                            textMemberId: 0,
                        }),
                    );
                }

                ctx.setState(produce((draft) => {
                    draft.newPersonForm.creating = false;
                    draft.newPersonFormResponse = response;
                }));
            },
            error: () => {
                ctx.setState(produce((draft) => {
                    draft.newPersonForm.creating = false;
                    draft.newPersonFormResponse = {
                        success: false,
                        validationResult: { errors: [{ propertyName: "personInfo", errorCode: "unknownError" }] },
                    } as SearchPersonForPermitResponse;
                }));
            },
        }));
    }

    @Action(PermitTeamActions.CreatePermitTeamTextMember)
    public CreatePermitTeamTextMember(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.CreatePermitTeamTextMember,
    ): Observable<CreatePermitTeamTextMemberResponse> {
        const command = {
            permitId: action.permitId,
            member: action.member,
        };

        return this.permitTeamService.createPermitTeamTextMember(command).pipe(
            tap((response: CreatePermitTeamTextMemberResponse) => {
                if (response.success) {
                    ctx.dispatch(
                        new PermitTeamActions.StagePerson({
                            birthDate: null,
                            certificateId: null,
                            certificateIdentifier: null,
                            concept: null,
                            email: null,
                            expirationDate: null,
                            hasValidCertificate: true,
                            firstName: action.member,
                            lastName: null,
                            mobile: null,
                            personId: null,
                            personalNumber: null,
                            hasForeignPersonalNumber: null,
                            signature: null,
                            endSignature: null,
                            hasAcceptedUserAgreement: null,
                            isPremiumIssuer: null,
                            isPremiumSuperintendent: null,
                            isFromColleague: null,
                            isSharedFromPremium: null,
                            allowExternalCertificates: null,
                            isTextMember: true,
                            textMemberId: 0,
                        }),
                    );
                }
            }),
        );
    }

    @Action(PermitTeamActions.CountersignPermit)
    public CountersignaturePermit(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.CountersignPermit,
    ): Observable<CountersignPermitResponse> {
        const command = {
            permitId: action.permitId,
            member: action.member,
            role: action.role,
            freeTextRole: action.freeTextRole,
            signatureData: action.signature,
        };

        return this.permitTeamService.countersignaturePermit(command)
            .pipe(
                tap(() => {
                    ctx.dispatch(new PermitTeamActions.GetTeam(action.permitId));
                })
            );
    }

    @Action(PermitTeamActions.CreateNewCertificateAndStagePerson)
    public createNewCertificateAndStagePerson(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.CreateNewCertificateAndStagePerson,
    ): Observable<CreateCertificateAndPersonResponse> {
        const command = {
            ...action.command,
            permitId: action.permitId,
            personId: action.person.personId,
            concept: Number(action.command.concept),
        };

        return this.permitTeamService.createCertificate(command).pipe(
            tap((response: CreateCertificateAndPersonResponse) => {
                if (response.success) {
                    ctx.dispatch(
                        new PermitTeamActions.StagePerson({
                            birthDate: action.person.birthDate,
                            certificateId: response.newCertificateId,
                            certificateIdentifier: command.certificateIdentifier,
                            concept: command.concept,
                            email: action.person.email,
                            expirationDate: command.expirationDate,
                            hasValidCertificate: true,
                            firstName: action.person.firstName,
                            lastName: action.person.lastName,
                            mobile: action.person.mobile,
                            personId: response.personId,
                            personalNumber: action.person.personalNumber,
                            hasForeignPersonalNumber: action.person.hasForeignPersonalNumber,
                            signature: action.person.signature,
                            endSignature: action.person.endSignature,
                            hasAcceptedUserAgreement: action.person.hasAcceptedUserAgreement,
                            isPremiumIssuer: action.person.isPremiumIssuer,
                            isPremiumSuperintendent: action.person.isPremiumSuperintendent,
                            isFromColleague: action.person.isFromColleague,
                            isSharedFromPremium: action.person.isSharedFromPremium,
                            allowExternalCertificates: action.person.allowExternalCertificates,
                            isTextMember: false,
                            textMemberId: 0,
                        }),
                    );
                }

                ctx.setState(produce((draft) => {
                    draft.createCertificateFormResponse = response;
                }));
            }),
        );
    }

    @Action(PermitTeamActions.SearchPersonForPermit)
    public searchPersonForPermit(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.SearchPersonForPermit,
    ): Observable<SearchPersonForPermitResponse> {
        ctx.setState(produce(ctx.getState(), (draft) => {
            draft.search.person = undefined;
            draft.search.errorMessage = undefined;
            draft.search.searching = true;
            draft.search.command = action.command;
        }));

        return this.permitTeamService.searchPersonForPermit(action.command).pipe(
            tap((result: SearchPersonForPermitResponse) => {
                if (!result.success) {
                    ctx.setState(produce(ctx.getState(), (draft) => {
                        draft.search.errorMessage = "notfound";
                        draft.search.searching = false;
                    }));

                    return;
                }

                const member = this.convertTeamMemberToMemberWithStatus(ctx, {
                    birthDate: result.person.birthDate,
                    certificateIdentifier: result.person.certificateIdentifier,
                    concept: result.person.concept,
                    email: result.person.email,
                    expirationDate: result.person.expirationDate,
                    firstName: result.person.firstName,
                    lastName: result.person.lastName,
                    mobile: result.person.mobile,
                    personId: result.person.personId,
                    personalNumber: result.person.personalNumber,
                    hasForeignPersonalNumber: result.person.hasForeignPersonalNumber,
                    certificateId: result.person.certificateId,
                    signature: undefined,
                    endSignature: undefined,
                    hasValidCertificate: true,
                    hasAcceptedUserAgreement: false,
                    isPremiumIssuer: false,
                    isPremiumSuperintendent: false,
                    isFromColleague: false,
                    isSharedFromPremium: false,
                    allowExternalCertificates: result.person.allowExternalCertificates,
                    isTextMember: false,
                    textMemberId: 0,
                });

                ctx.setState(produce(ctx.getState(), (draft) => {
                    draft.search.person = member;
                    draft.search.searching = false;
                }));
            }),
        );
    }

    @Action(PermitTeamActions.ClearCreateCertificateResponse)
    public clearCreateCertificateResponse(ctx: StateContext<PermitTeamStateModel>): void {
        ctx.setState(produce((draft) => {
            if (draft.createCertificateFormResponse) {
                draft.createCertificateFormResponse.validationResult = undefined;
                draft.createCertificateFormResponse.certificateId = undefined;
                draft.createCertificateFormResponse.newCertificateId = undefined;
                draft.createCertificateFormResponse.personId = undefined;
                draft.createCertificateFormResponse.success = undefined;
            }
        }));
    }

    @Action(PermitTeamActions.FindPersonBA)
    public findPersonBA(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.FindPersonBA,
    ): Observable<FindCertifiedPersonResult> {
        ctx.setState(produce(ctx.getState(), (draft) => {
            draft.search.person = undefined;
            draft.search.errorMessage = undefined;
            draft.search.searching = true;
            draft.search.query = action.query;
        }));

        return this.permitTeamService.findPersonBA(action.query).pipe(tap((result: FindCertifiedPersonResult) => {
            const member = this.convertTeamMemberToMemberWithStatus(ctx, {
                birthDate: result.user.birthDate,
                certificateId: result.id,
                certificateIdentifier: result.certificateIdentifier,
                concept: result.concept,
                email: result.user.email,
                expirationDate: result.expirationDate,
                hasValidCertificate: true,
                firstName: result.user.firstName,
                lastName: result.user.lastName,
                mobile: result.user.mobile,
                personId: 0,
                personalNumber: result.user.personalNumber,
                hasForeignPersonalNumber: result.user.hasForeignPersonalNumber,
                signature: undefined,
                endSignature: undefined,
                hasAcceptedUserAgreement: false,
                isPremiumIssuer: false,
                isPremiumSuperintendent: false,
                isFromColleague: false,
                isSharedFromPremium: false,
                allowExternalCertificates: false,
                isTextMember: false,
                textMemberId: 0,
            });

            ctx.setState(produce(ctx.getState(), (draft) => {
                draft.search.person = member;
                draft.search.errorMessage = result.status != CertificateStatus.ValidCertificate ? "notfound" : null;
                draft.search.searching = false;
            }));
        }));
    }

    @Action(PermitTeamActions.FindPerson)
    public findPerson(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.FindPerson,
    ): Observable<FindCertifiedPersonResult> {
        ctx.setState(produce(ctx.getState(), (draft) => {
            draft.search.person = undefined;
            draft.search.errorMessage = undefined;
            draft.search.searching = true;
            draft.search.query = action.query;
        }));

        return this.permitTeamService.findPerson(action.query).pipe(tap((result: FindCertifiedPersonResult) => {
            if (!result || result.status == CertificateStatus.CertificateNotFound) {
                ctx.setState(produce(ctx.getState(), (draft) => {
                    draft.search.errorMessage = "notfound";
                    draft.search.searching = false;
                }));

                return;
            }

            if (result.status == CertificateStatus.ValidCertificate) {
                if (action.query.role == PersonRole.PermitIssuer && result.concept !== ConceptType.HetaArbeten) {
                    ctx.setState(produce(ctx.getState(), (draft) => {
                        draft.search.errorMessage = "wrongconcept";
                        draft.search.searching = false;
                    }));

                    return;
                }

                const member = this.convertTeamMemberToMemberWithStatus(ctx, {
                    birthDate: result.user.birthDate,
                    certificateId: result.id,
                    certificateIdentifier: result.certificateIdentifier,
                    concept: result.concept,
                    email: result.user.email,
                    expirationDate: result.expirationDate,
                    hasValidCertificate: true,
                    firstName: result.user.firstName,
                    lastName: result.user.lastName,
                    mobile: result.user.mobile,
                    personId: 0,
                    personalNumber: result.user.personalNumber,
                    hasForeignPersonalNumber: result.user.hasForeignPersonalNumber,
                    signature: undefined,
                    endSignature: undefined,
                    hasAcceptedUserAgreement: false,
                    isPremiumIssuer: false,
                    isPremiumSuperintendent: false,
                    isFromColleague: false,
                    isSharedFromPremium: false,
                    allowExternalCertificates: false,
                    isTextMember: false,
                    textMemberId: 0,
                });

                ctx.setState(produce(ctx.getState(), (draft) => {
                    draft.search.person = member;
                    draft.search.searching = false;
                }));
            } else {
                ctx.setState(produce(ctx.getState(), (draft) => {
                    draft.search.errorMessage = "notvalid";
                    draft.search.searching = false;
                }));
            }
        }));
    }

    @Action(PermitTeamActions.GetRecentTeamMembers)
    public getRecentTeamMembers(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.GetRecentTeamMembers,
    ): Observable<RecentTeamMembersResponse> {
        if (action.restart) {
            ctx.patchState({
                recentTeamMembers: [],
                recentTeamMembersFullyLoaded: false,
            });
        }

        const skip = ctx.getState()?.recentTeamMembers?.length ?? 0;

        return this.permitTeamService.getRecentTeamMembers(
            action.permitId,
            skip,
            action.take,
            action.includeColleagues,
            action.includeSharedFromPremium,
            action.sort,
            action.sortByDescending,
            action.query,
        ).pipe(tap((response: RecentTeamMembersResponse) => {
            const teamMembersWithStatus: PermitPersonWithStatus[] = [];

            response.members.forEach((member) => {
                const teamMemberWithStatus: PermitPersonWithStatus = this.convertTeamMemberToMemberWithStatus(
                    ctx,
                    member,
                );
                teamMembersWithStatus.push(teamMemberWithStatus);
            });

            ctx.setState(produce(ctx.getState(), (draft) => {
                const result = [...draft.recentTeamMembers ?? [], ...teamMembersWithStatus];
                draft.recentTeamMembers = result;
                draft.recentTeamMembersSearchResult = result;
                draft.recentTeamMembersFullyLoaded = !response.hasMore;
                draft.hasColleagueMembers = response.hasColleagueMembers;
                draft.hasSharedFromPremiumMembers = response.hasSharedFromPremiumMembers;
            }));
        }));
    }

    @Action(PermitTeamActions.GetTeam)
    public getTeam(ctx: StateContext<PermitTeamStateModel>, action: PermitTeamActions.GetTeam): Observable<PermitTeam> {
        return this.permitTeamService.getTeam(action.permitId).pipe(tap((result: PermitTeam) => {
            ctx.setState(produce(ctx.getState(), (draft) => {
                draft.team = result;
            }));
        }));
    }

    @Action(PermitTeamActions.RemovePerson)
    public removePerson(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.RemovePerson,
    ): Observable<BaseValidationResult> {
        return this.permitTeamService.removePerson(action.command).pipe(tap(() => {
            ctx.dispatch(new PermitTeamActions.GetTeam(action.command.permitId));
        }));
    }

    @Action(PermitTeamActions.ResetFind)
    public resetFind(ctx: StateContext<PermitTeamStateModel>) {
        return ctx.patchState({ search: searchDefaults });
    }

    @Action(PermitTeamActions.ResetNewPersonForm)
    public resetNewPersonForm(ctx: StateContext<PermitTeamStateModel>) {
        return ctx.patchState({ newPersonForm: newPersonFormDefaults, certificateIdentifier: undefined });
    }

    @Action(PermitTeamActions.ResetNewPersonResponse)
    public resetNewPersonResponse(ctx: StateContext<PermitTeamStateModel>) {
        return ctx.patchState({ newPersonFormResponse: undefined });
    }

    @Action(PermitTeamActions.ResetNewPersonFormKeepCertId)
    public resetNewPersonFormKeepCertId(ctx: StateContext<PermitTeamStateModel>) {
        return ctx.setState(produce(ctx.getState(), (draft) => {
            const certificateIdentifier = draft.newPersonForm.model?.certificateIdentifier ?? draft.certificateIdentifier;
            draft.newPersonForm = {
                ...newPersonFormDefaults,
                model: { certificateIdentifier } as PersonInfo,
            };
        }));
    }

    @Action(PermitTeamActions.SetConcept)
    public setConcept(ctx: StateContext<PermitTeamStateModel>, action: PermitTeamActions.SetConcept) {
        return ctx.patchState({ currentConcept: action.concept });
    }

    @Action(PermitTeamActions.SetNewPersonForm)
    public setNewPersonForm(ctx: StateContext<PermitTeamStateModel>, action: PermitTeamActions.SetNewPersonForm) {
        return ctx.setState(produce(ctx.getState(), (draft) => {
            const newPersonForm = {
                certificateId: action.person.certificateId,
                personId: action.person.personId,
                firstName: action.person.firstName,
                lastName: action.person.lastName,
                email: action.person.email,
                mobile: action.person.mobile,
                personalNumber: draft.newPersonForm.model?.personalNumber,
                birthDate: action.person.birthDate,
                certificateIdentifier: draft.certificateIdentifier,
            } as PersonInfo;
            draft.newPersonForm.model = newPersonForm;
        }));
    }

    @Action(PermitTeamActions.SetNewPersonFormBA)
    public setNewPersonFormBA(ctx: StateContext<PermitTeamStateModel>, action: PermitTeamActions.SetNewPersonForm) {
        return ctx.setState(produce(ctx.getState(), (draft) => {
            const newPersonForm = {
                certificateId: action.person.certificateId,
                personId: action.person.personId,
                firstName: action.person.firstName,
                lastName: action.person.lastName,
                email: action.person.email,
                mobile: action.person.mobile,
                personalNumber: action.person.personalNumber,
                birthDate: action.person.birthDate,
                certificateIdentifier: action.person.certificateIdentifier,
                expirationDate: action.person.expirationDate,
            } as PersonInfo;
            draft.newPersonForm.model = newPersonForm;
        }));
    }

    @Action(PermitTeamActions.SetPersonalNumber)
    public setPersonalNumber(ctx: StateContext<PermitTeamStateModel>, action: PermitTeamActions.SetPersonalNumber) {
        return ctx.setState(produce(ctx.getState(), (draft) => {
            if (!draft.newPersonForm.model) {
                draft.newPersonForm.model = {} as PersonInfo;
            }
            draft.newPersonForm.model.personalNumber = action.personalNumber;
        }));
    }

    @Action(PermitTeamActions.SetHasForeignPersonalNumber)
    public setHasForeignPersonalNumber(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.SetHasForeignPersonalNumber,
    ) {
        return ctx.setState(produce(ctx.getState(), (draft) => {
            if (!draft.newPersonForm.model) {
                draft.newPersonForm.model = {} as PersonInfo;
            }
            draft.newPersonForm.model.hasForeignPersonalNumber = action.hasForeignPersonalNumber;
        }));
    }

    @Action(PermitTeamActions.SetEmail)
    public setEmail(ctx: StateContext<PermitTeamStateModel>, action: PermitTeamActions.SetEmail) {
        return ctx.setState(produce(ctx.getState(), (draft) => {
            if (!draft.newPersonForm.model) {
                draft.newPersonForm.model = {} as PersonInfo;
            }
            draft.newPersonForm.model.email = action.email;
        }));
    }

    @Action(PermitTeamActions.SetCertificateIdentifier)
    public setCertificateIdentifier(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.SetCertificateIdentifier,
    ) {
        return ctx.setState(produce(ctx.getState(), (draft) => {
            if (!draft.newPersonForm.model) {
                draft.newPersonForm.model = {} as PersonInfo;
            }
            draft.newPersonForm.model.certificateIdentifier = action.certificateIdentifier;
            draft.certificateIdentifier = action.certificateIdentifier;
        }));
    }

    @Action(PermitTeamActions.SetAddPersonView)
    public setAddPersonView(ctx: StateContext<PermitTeamStateModel>, action: PermitTeamActions.SetAddPersonView) {
        return ctx.patchState({ stageRole: action.role });
    }

    @Action(PermitTeamActions.ToggleAllowSwitching)
    public toggleAllowSwitching(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.ToggleAllowSwitching,
    ) {
        return this.permitTeamService.toggleAllowSwitching(action.command).pipe(tap(() => {
            ctx.dispatch(new PermitTeamActions.GetTeam(action.command.permitId));
        }));
    }

    @Action(PermitTeamActions.SetFireWatchersNotNeeded)
    public setFireWatchersNotNeeded(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.SetFireWatchersNotNeeded,
    ) {
        return this.permitTeamService.setFireWatchersNotNeeded(action.command).pipe(tap(() => {
            ctx.dispatch(new PermitTeamActions.GetTeam(action.command.permitId));
        }));
    }

    @Action(PermitTeamActions.SignPermitWithSignature)
    public signPermitByPerson(
        ctx: StateContext<PermitTeamStateModel>,
        action: PermitTeamActions.SignPermitWithSignature,
    ) {
        return this.permitTeamService.signPermitWithSignature(action.command).pipe(tap(() => {
            ctx.dispatch(new PermitTeamActions.GetTeam(action.command.permitId));
        }));
    }

    @Action(PermitTeamActions.StagePerson)
    public stagePerson(ctx: StateContext<PermitTeamStateModel>, action: PermitTeamActions.StagePerson) {
        return ctx.setState(produce(ctx.getState(), (draft) => {
            if (!draft.stagedPersons) {
                draft.stagedPersons = [action.person];
            } else {
                if (
                    !draft.stagedPersons.find((_) => _.certificateId == action.person.certificateId)
                    || action.person.certificateId == null
                ) {
                    draft.stagedPersons.push(action.person);
                }
            }
        }));
    }

    @Action(PermitTeamActions.UnstageAll)
    public unstageAll(ctx: StateContext<PermitTeamStateModel>) {
        return ctx.setState(produce(ctx.getState(), (draft) => {
            draft.stagedPersons = undefined;
        }));
    }

    @Action(PermitTeamActions.UnstagePerson)
    public unstagePerson(ctx: StateContext<PermitTeamStateModel>, action: PermitTeamActions.UnstagePerson) {
        return ctx.setState(produce(ctx.getState(), (draft) => {
            if (draft.stagedPersons) {
                draft.stagedPersons = draft.stagedPersons.filter((_) => _.certificateId != action.certificateId);
            }
        }));
    }

    private convertTeamMemberToMemberWithStatus(
        ctx: StateContext<PermitTeamStateModel>,
        member: PermitPerson,
    ): PermitPersonWithStatus {
        const currentState = ctx.getState();
        const currentRole = currentState.stageRole;
        const currentTeam = currentState.team;

        const date: Date = new Date();
        const now = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        const soon = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        soon.setMonth(soon.getMonth() + 3);

        const teamMemberWithStatus: PermitPersonWithStatus = member as PermitPersonWithStatus;

        if (new Date(teamMemberWithStatus.expirationDate) < now) {
            teamMemberWithStatus.certificateExpired = true;
        }
        if (new Date(teamMemberWithStatus.expirationDate) < soon) {
            teamMemberWithStatus.certificateExpireSoon = true;
        }

        teamMemberWithStatus.canBeAdded = canBeAdded();

        function canBeAdded(): boolean {
            if (currentRole === PersonRole.PermitIssuer) {
                if (currentTeam.hotWorkers?.find((_) => _.certificateId == teamMemberWithStatus.certificateId)) {
                    return false;
                }

                if (currentTeam.permitIssuer?.certificateId == teamMemberWithStatus.certificateId) {
                    return false;
                }
            }

            if (currentRole === PersonRole.Superintendent) {
                if (currentTeam.superintendent?.certificateId == teamMemberWithStatus.certificateId) {
                    return false;
                }
            }

            if (currentRole === PersonRole.HotWorker) {
                if (currentTeam.permitIssuer?.certificateId == teamMemberWithStatus.certificateId) {
                    return false;
                }

                if (currentTeam.superintendent?.certificateId == teamMemberWithStatus.certificateId) {
                    return false;
                }

                if (currentTeam.fireWatchers?.find((_) => _.certificateId == teamMemberWithStatus.certificateId)) {
                    return false;
                }

                if (currentTeam.hotWorkers?.find((_) => _.certificateId == teamMemberWithStatus.certificateId)) {
                    return false;
                }
            }

            if (currentRole === PersonRole.FireWatcher) {
                if (currentTeam.hotWorkers?.find((_) => _.certificateId == teamMemberWithStatus.certificateId)) {
                    return false;
                }

                if (currentTeam.fireWatchers?.find((_) => _.certificateId == teamMemberWithStatus.certificateId)) {
                    return false;
                }
            }

            if (currentRole === PersonRole.PostFireWatcher) {
                if (currentTeam.postFireWatchers?.find((_) => _.certificateId == teamMemberWithStatus.certificateId)) {
                    return false;
                }
            }

            return true;
        }

        return teamMemberWithStatus;
    }
}
